import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {PageOffering} from '@core/model/side-nav.model';
import {UtilsService} from "@shared-services/utils.service";
import {
  PaymentConfigData,
  ProductSupplier,
  SupplierSourceConfig,
  TransactionSource,
  TransactionSourceConfig
} from '../../../leasing.model';
import {LeasePosSettingsComponent} from "../lease-pos-settings.component";
import {ConsoleContextService} from "@security/console-context.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {
  convertToDateObject,
  endDateMaxValue,
  endDateMinValue,
  startDateMaxValue,
  startDateMinValue
} from '@features/leasing/leasing.periods.util';
import {FormControl} from '@angular/forms';
import {PaymentTypeName} from '@features/settings/model/payments.model';
import {
  isAcceptableBillingDayOfMonth,
  isBillingDayOfMonthEmpty,
  isValidBillingDayOfMonth,
  MAX_BILLING_DAY_OF_MONTH,
  MIN_BILLING_DAY_OF_MONTH
} from '@features/leasing/pos-billing-date.util';

@Component({
  selector: 'app-pos-software-settings-dialog',
  templateUrl: './pos-software-settings-dialog.component.html',
  styles: [`
    /* The form is long enough to overflow the dialog, and the dialog itself does not scroll,
       so the sections scroll while the Cancel/Save actions stay put below them. */
    .pos-dialog-scroll {
      max-height: 60vh;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .expansion-panel-title {
      font-size: 10px !important;
    }

    /* Angular Material tokenises expansion-panel heights and typography but NOT padding, and the
       defaults (24px sides, 48/64px header, 16px inter-panel gap) are far too airy for a dialog.
       Scoped to .compact-accordion so the override cannot reach any other panel in the app. */
    :host ::ng-deep .compact-accordion .mat-expansion-panel-header {
      padding: 0 12px;
      height: 40px;
    }

    :host ::ng-deep .compact-accordion .mat-expansion-panel-header.mat-expanded {
      height: 40px;
    }

    :host ::ng-deep .compact-accordion .mat-expansion-panel-body {
      padding: 0 12px 12px;
    }

    :host ::ng-deep .compact-accordion .mat-expansion-panel-header-title,
    :host ::ng-deep .compact-accordion .mat-expansion-panel-header-description {
      margin-right: 8px;
    }

    /* Material adds 16px above and below an expanded panel inside an accordion. */
    :host ::ng-deep .compact-accordion .mat-expansion-panel-spacing {
      margin: 0;
    }
  `],
})
export class PosSoftwareSettingsDialogComponent {
  singleLeaseConfig: TransactionSourceConfig | null = null;
  supplierConfig: SupplierSourceConfig | null = null;
  netCashReferenceCtrl: FormControl = new FormControl();
  /**
   * CS-1369 — Lightspeed plan renewal day. Reactive (rather than ngModel like its neighbours) so
   * the optional-but-must-be-valid rule drives the form field's error state and <mat-error>.
   */
  billingDayOfMonthCtrl: FormControl = new FormControl(null, control =>
    isAcceptableBillingDayOfMonth(control.value) ? null : {billingDayOfMonth: true});
  selectedNetcashRef: string | null = null;
  filteredNetcashRefs: string[] = [];
  offering: PageOffering;
  isEditing: boolean = false;
  canAccess: boolean = false;
  allConfigs: PaymentConfigData[] = [];
  allParentConfigs: PaymentConfigData[] = [];

  offeringSupplierConfigs: { label: string, code: string, uuid: string }[] = [];
  supplierLeaseConfigs: { label: string, code: string, uuid: string }[] = [];
  availableSuppliers: ProductSupplier[] = [];
  assignedSuppliers: ProductSupplier[] = [];
  offeringConfig: boolean = false;

  parentComponent: LeasePosSettingsComponent;

  posType: TransactionSource = TransactionSource.VEND;

  posTypes = [
    { label: 'Lightspeed Retail X-Series (Vend)', source: TransactionSource.VEND },
    { label: 'Lightspeed Retail X-Series (Vend) Supplier', source: TransactionSource.VEND_SUPPLIER_OFFERING },
    { label: 'Lightspeed Retail X-Series (Vend) Supplier', source: TransactionSource.VEND_SUPPLIER_LEASE },
    {label: 'Poster', source: TransactionSource.POSTER},
    {label: 'Netcash', source: TransactionSource.NETCASH}
  ];

  paymentTypes = [
    {label: 'YOCO', source: PaymentTypeName.YOCO},
    {label: 'Standard Bank', source: PaymentTypeName.STD_BANK},
  ]
  allowedPosTypes: {label: string, source: TransactionSource}[] = [];
  allowedPaymentTypes: { label: string, source: PaymentTypeName }[] = []
  activationStatus: string | null = null;
  isNewEdit: boolean = false;

  loading$ = this.loader.loading$;

  constructor(public dialogRef: MatDialogRef<PosSoftwareSettingsDialogComponent>,
              public currentContext: ConsoleContextService,
              public utils: UtilsService,
              public loader: LoadingService,
              @Inject(MAT_DIALOG_DATA) public data: any) {
    this.posType = data.posType;
    this.allowedPosTypes = this.posTypes.filter(f => data.allowedPosTypes.includes(f.source));
    this.parentComponent = data.parentComponent;
    this.offeringConfig = data.offeringConfig;
    // The caller hands us the very objects its tables render. Everything below converts the unix
    // dates into Date objects for the datepickers, so edit them on copies — otherwise closing the
    // dialog (saved or cancelled) leaves Date objects behind and the table's unix date formatting
    // renders blank until the page is reloaded.
    this.allConfigs = (data.allConfigs ?? []).map((config: PaymentConfigData) =>
      Object.assign(new PaymentConfigData(), config));
    this.allParentConfigs = data.allParentConfigs;
    this.isEditing = data.isEditing;
    this.activationStatus = data.activationStatus;
    this.allConfigs.forEach(config => {
      config.startDate = convertToDateObject(config.startDate);
      config.endDate = convertToDateObject(config.endDate);
      config.earliestTransactionDate = convertToDateObject(config.earliestTransactionDate);
      config.latestTransactionDate = convertToDateObject(config.latestTransactionDate);
    });

    this.netCashReferenceCtrl.valueChanges.subscribe((value: any) => {
      this.filterNetcashReferences(value);
    });

    if (data.singleLeaseConfig && this.activationStatus == 'ACTIVATION_REQUESTED') {
      this.isNewEdit = true;
    }

    if (data.singleLeaseConfig) {
      this.singleLeaseConfig = Object.assign(new TransactionSourceConfig(), data.singleLeaseConfig);
      this.singleLeaseConfig!.transactionReferences = [...(data.singleLeaseConfig.transactionReferences ?? [])];
      this.singleLeaseConfig!.paymentSourceName = this.posType;

      if (this.allConfigs.length == 0) {
        this.singleLeaseConfig!.startDate = convertToDateObject(data.leaseStartDate);
        this.singleLeaseConfig!.endDate = convertToDateObject(data.leaseEndDate);
      } else {
        this.singleLeaseConfig!.startDate = convertToDateObject(this.singleLeaseConfig!.startDate);
        this.singleLeaseConfig!.endDate = convertToDateObject(this.singleLeaseConfig!.endDate);
      }
      this.singleLeaseConfig!.earliestTransactionDate = convertToDateObject(this.singleLeaseConfig!.earliestTransactionDate);
      this.singleLeaseConfig!.latestTransactionDate = convertToDateObject(this.singleLeaseConfig!.latestTransactionDate);
      this.singleLeaseConfig!.minStartDate = convertToDateObject(this.singleLeaseConfig!.minStartDate);
      this.singleLeaseConfig!.maxEndDate = convertToDateObject(this.singleLeaseConfig!.maxEndDate);
      if (this.singleLeaseConfig?.transactionReferences) {
        this.filteredNetcashRefs = [...this.singleLeaseConfig.transactionReferences]
      }
    } else {
      this.singleLeaseConfig = new TransactionSourceConfig();
      this.singleLeaseConfig!.paymentSourceName = this.posType;
    }

    if (data.supplierConfig) {
      this.supplierConfig = Object.assign(new SupplierSourceConfig(), data.supplierConfig);
      this.supplierConfig!.suppliers = data.supplierConfig.suppliers ? [...data.supplierConfig.suppliers] : null;
      this.supplierConfig!.startDate = convertToDateObject(this.supplierConfig!.startDate);
      this.supplierConfig!.endDate = convertToDateObject(this.supplierConfig!.endDate);
      this.supplierConfig!.earliestTransactionDate = convertToDateObject(this.supplierConfig!.earliestTransactionDate);
      this.supplierConfig!.latestTransactionDate = convertToDateObject(this.supplierConfig!.latestTransactionDate);
      this.supplierConfig!.minStartDate = convertToDateObject(this.supplierConfig!.minStartDate);
      this.supplierConfig!.maxEndDate = convertToDateObject(this.supplierConfig!.maxEndDate);
    } else {
      this.supplierConfig = new SupplierSourceConfig();
    }

    if (this.supplierConfig && this.supplierConfig.paymentConfigUuid) {
      this.loadConfigSuppliers(this.supplierConfig!.paymentConfigUuid, this.posType);
    }

    if (this.isEditing && this.supplierConfig && this.posType == TransactionSource.VEND_SUPPLIER_OFFERING) {
      this.loadConfigSuppliers(this.supplierConfig?.paymentConfigUuid!, this.posType);
    }

    if (this.isEditing && this.supplierConfig && this.supplierConfig.suppliers) {
      this.assignedSuppliers = this.supplierConfig.suppliers!.map(m => m);
    }

    this.offering = data.offering;
    this.canAccess = this.currentContext.checkUserFeatures(['leasing_internal_admin'], this.offering.offeringUuid);

    // CS-1369 — seed the plan renewal day from the stored config and mirror edits back onto it.
    this.billingDayOfMonthCtrl.setValue(this.singleLeaseConfig?.billingDayOfMonth ?? null, {emitEvent: false});
    this.billingDayOfMonthCtrl.valueChanges.subscribe(value => {
      if (this.singleLeaseConfig) {
        this.singleLeaseConfig.billingDayOfMonth = isValidBillingDayOfMonth(value) ? Number(value) : null;
      }
    });
    // Editing stays gated by leasing_internal_admin only — unlike the credential fields it must
    // remain editable on an already-activated config, which is exactly when we prompt for it.
    if (!this.canAccess) {
      this.billingDayOfMonthCtrl.disable();
    }

    if (data.allowedPosTypes.includes(TransactionSource.VEND_SUPPLIER_OFFERING)) {
      this.loadOfferingPaymentConfigs();
    }
    if (data.allowedPosTypes.includes(TransactionSource.VEND_SUPPLIER_LEASE)) {
      this.loadSupplierLeasePaymentConfigs();
    }
    this.setAllowedPaymentTypes();
  }

  /**
   * The single-lease (VEND/POSTER) form is long enough to need grouping, so it renders as an
   * accordion. Every other payment source keeps its original flat layout.
   */
  get showSingleLeaseSections(): boolean {
    return (this.posType == TransactionSource.VEND || this.posType == TransactionSource.POSTER)
      && !!this.singleLeaseConfig;
  }

  /**
   * CS-1369 — the Lightspeed plan renewal day is only captured for the single-lease VEND config.
   * Poster and the supplier-based configs (which use SupplierSourceConfig) are out of scope.
   */
  get showBillingDayOfMonth(): boolean {
    return this.posType == TransactionSource.VEND && !!this.singleLeaseConfig;
  }

  /**
   * Optional field: blank is fine (the renewal date is often unknown at initial setup), but a
   * value that has been entered must be a real day of the month.
   */
  get billingDayOfMonthInvalid(): boolean {
    if (!this.showBillingDayOfMonth) {
      return false;
    }
    return !isAcceptableBillingDayOfMonth(this.billingDayOfMonthCtrl.value);
  }

  get billingDayOfMonthErrorMessage(): string {
    return `Enter a day of the month between ${MIN_BILLING_DAY_OF_MONTH} and ${MAX_BILLING_DAY_OF_MONTH}, or leave it blank.`;
  }

  get billingDayOfMonthMissing(): boolean {
    return this.showBillingDayOfMonth && isBillingDayOfMonthEmpty(this.billingDayOfMonthCtrl.value);
  }

  get canSave() {
    // Blank is allowed; a value that has been typed must be valid before the dialog can save.
    if (this.billingDayOfMonthInvalid) {
      return false;
    }
    if ((this.posType == TransactionSource.VEND || this.posType == TransactionSource.POSTER) && this.singleLeaseConfig) {
      return !!this.singleLeaseConfig.paymentTypeName
        && !!this.singleLeaseConfig.paymentSourceName
        && !!this.singleLeaseConfig.domainPrefix
        && !!this.singleLeaseConfig.outletId
        && !!this.singleLeaseConfig.accessToken
        && this.singleLeaseConfig.domainPrefix.trim().length != 0
        && this.singleLeaseConfig.outletId.trim().length != 0
        && this.singleLeaseConfig.accessToken.trim().length != 0
    } else if (this.posType == TransactionSource.NETCASH && this.singleLeaseConfig?.transactionReferences) {
      return this.singleLeaseConfig.startDate != null && this.singleLeaseConfig.transactionReferences.length != 0
    } else {
      if (this.supplierConfig) {
        return !!this.supplierConfig.paymentConfigUuid
          && !!this.supplierConfig.suppliers
          && this.supplierConfig.suppliers.length > 0
      }
    }
    return false;
  }

  saveChanges(): void {
    if (this.posType == TransactionSource.NETCASH) {
      this.singleLeaseConfig!.paymentSourceName = TransactionSource.NETCASH;
    }
    this.dialogRef.close({save: true, singleLeaseConfig: this.singleLeaseConfig, supplierConfig: this.supplierConfig});
  }

  cancelChanges(): void {
    this.dialogRef.close({save: false, singleLeaseConfig: null})
  }

  loadConfigSuppliers(uuid: string, source: TransactionSource) {
    if (source == TransactionSource.VEND_SUPPLIER_LEASE) this.parentComponent.loadPosSuppliers(uuid);
    if (source == TransactionSource.VEND_SUPPLIER_OFFERING) this.parentComponent.loadOfferingSuppliers(uuid);
  }

  close() {
    this.dialogRef.close({save: false, singleLeaseConfig: null});
  }

  get canEdit() {
    return ((this.posType != TransactionSource.VEND && this.posType != TransactionSource.POSTER)) || (this.canAccess);
  }

  loadOfferingPaymentConfigs() {
    this.offeringSupplierConfigs = this.parentComponent.offeringPaymentConfigs.map(m => {
      const displayName = JSON.parse(m.transactionSourceConfig!)['displayName'];
      const domainPrefix = JSON.parse(m.transactionSourceConfig!)['domainPrefix'];
      return { label: displayName, code: domainPrefix, uuid: m.uuid! };
    })
  }

  loadSupplierLeasePaymentConfigs() {
    this.supplierLeaseConfigs = this.parentComponent.parentPaymentConfigs.map(m => {
      const displayName = JSON.parse(m.transactionSourceConfig!)['displayName'];
      const domainPrefix = JSON.parse(m.transactionSourceConfig!)['domainPrefix'];
      return {label: displayName, code: domainPrefix, uuid: m.uuid!};
    });
  }

  startDateMinValue(leaseType: string): any {
    if (leaseType === 'single_lease') {
      if (this.singleLeaseConfig!.minStartDate) {
        return this.singleLeaseConfig!.minStartDate;
      }
      return startDateMinValue(this.singleLeaseConfig, this.allConfigs, this.isEditing);
    }
    if (leaseType === 'supplier_lease') {
      if (this.supplierConfig!.minStartDate) {
        return this.supplierConfig!.minStartDate;
      }
      return startDateMinValue(this.supplierConfig, this.allConfigs, this.isEditing);
    }
    return '';
  }

  startDateMaxValue(leaseType: string): any {
    if (leaseType === 'single_lease') {
      return startDateMaxValue(this.singleLeaseConfig, this.allConfigs);
    }
    if (leaseType === 'supplier_lease') {
      return startDateMaxValue(this.supplierConfig, this.allConfigs);
    }
    return '';
  }

  endDateMinValue(leaseType: string): any {
    if (leaseType === 'single_lease') {
      return endDateMinValue(this.singleLeaseConfig, this.allConfigs);
    }
    if (leaseType === 'supplier_lease') {
      return endDateMinValue(this.supplierConfig, this.allConfigs);
    }
    return '';
  }

  endDateMaxValue(leaseType: string): any {

    if (leaseType === 'single_lease') {
      if (this.singleLeaseConfig!.maxEndDate) {
        return this.singleLeaseConfig!.maxEndDate;
      }
      return endDateMaxValue(this.singleLeaseConfig, this.allConfigs);
    }
    if (leaseType === 'supplier_lease') {
      if (this.supplierConfig!.maxEndDate) {
        return this.supplierConfig!.maxEndDate;
      }
      return endDateMaxValue(this.supplierConfig, this.allConfigs);
    }

    return '';
  }

  onStartDateChange(newDate: Date, leaseType: string) {
    if (this.singleLeaseConfig != null && leaseType === 'single_lease') {
      this.singleLeaseConfig.startDate = newDate;
    }
    if (this.supplierConfig != null && leaseType === 'supplier_lease') {
      this.supplierConfig.startDate = newDate;
    }
  }

  protected readonly TransactionSource = TransactionSource;

  onEndDateChange(newDate: Date, leaseType: string) {
    if (this.singleLeaseConfig != null && leaseType === 'single_lease') {
      this.singleLeaseConfig.endDate = newDate;
    }
    if (this.supplierConfig != null && leaseType === 'supplier_lease') {
      this.supplierConfig.endDate = newDate;
    }
  }

  getAvailableSuppliers(all: ProductSupplier[]): ProductSupplier[] {
    const seen = new Set<string>();
    return all.filter((item) => {
      if (seen.has(item.uuid)) {
        return false;
      }
      seen.add(item.uuid);
      return true;
    });
  }

  addTransactionReference(): void {
    if (this.singleLeaseConfig?.transactionReferences) {
      const value = this.netCashReferenceCtrl.value?.trim();
      if (value && !this.singleLeaseConfig?.transactionReferences.includes(value)) {
        this.singleLeaseConfig!.transactionReferences.push(value);
        this.filterNetcashReferences(value); // refresh list
      }
      this.selectedNetcashRef = value;
      this.netCashReferenceCtrl.setValue('');
    }
  }

  selectItem(event: any) {
    this.selectedNetcashRef = event.option.value;
    this.netCashReferenceCtrl.setValue('');
  }

  removeTransactionReference(item: string): void {
    if (this.singleLeaseConfig?.transactionReferences) {
      this.singleLeaseConfig.transactionReferences = this.singleLeaseConfig.transactionReferences.filter(i => i !== item);
      this.filterNetcashReferences(this.netCashReferenceCtrl.value);
      if (this.selectedNetcashRef === item) {
        this.selectedNetcashRef = null;
      }
    }
  }

  filterNetcashReferences(value: string) {
    if (this.singleLeaseConfig?.transactionReferences) {
      const filterValue = value?.toLowerCase() || '';
      this.filteredNetcashRefs = this.singleLeaseConfig.transactionReferences.filter(item =>
        item.toLowerCase().includes(filterValue)
      );
    }
  }

  changePosType($event: any): void {
    this.singleLeaseConfig!.paymentSourceName = $event
    this.posType = $event
    this.setAllowedPaymentTypes();
  }

  private setAllowedPaymentTypes(): void {
    if (this.posType == TransactionSource.NETCASH) {
      this.allowedPaymentTypes = this.paymentTypes.filter(fl => fl.source == PaymentTypeName.STD_BANK);
    } else {
      this.allowedPaymentTypes = this.paymentTypes.filter(fl => fl.source == PaymentTypeName.YOCO);
    }
  }
}
